import { put, call, all, takeEvery, delay } from "redux-saga/effects";
import axios from "axios";
import objectionActions from "./actions";
import commonActions from "../common/actions";
import { EncryptFunction } from "../../utils/cryptoFunction";
import { handleDecodeJSON } from "../../utils/encodeDecodeJson";
import { InternalError } from "../../common/ErrorMessage";
import { OBJECTION_PAYMENT_STORAGE_KEY } from "../../components/objection/constants";

const objectionSaga = function* () {
  yield all([
    yield takeEvery(objectionActions.GET_OBJ_NOTES, getObjNotes),
    yield takeEvery(objectionActions.GET_OBJ_QUESTIONS, getObjQuestions),
    yield takeEvery(objectionActions.GET_OBJ_VIEW, getObjView),
    yield takeEvery(objectionActions.RAISE_OBJ, raiseObj),
    yield takeEvery(objectionActions.DELETE_OBJ, deleteObj),
    yield takeEvery(objectionActions.GET_OBJ_SUMMARY, getObjSummary),
    yield takeEvery(objectionActions.GET_OBJ_PAYMENT_SUMMARY, getObjPaymentSummary),
    yield takeEvery(objectionActions.CREATE_OBJ_ORDER, createObjOrder),
    yield takeEvery(objectionActions.VERIFY_OBJ_PAYMENT, verifyObjPayment),
    yield takeEvery(objectionActions.GET_OBJ_RECEIPT_LIST, getObjReceiptList),
  ]);
};

// Validation failures come back as { message: "Validation failed", errors: [...] }.
const toApiError = (err) => ({
  message:
    err?.response?.data?.errors?.[0] ||
    err?.response?.data?.message ||
    err?.message,
});

// Response (200):
//   closed window -> { isOpen: false, message }
//   absent        -> { rollno, isPresent: false, instructions: null, message }
//   present       -> { rollno, isPresent: true, instructions, message: null }
// Any other failure is a non-200 and is shown as a toast.
const getObjNotes = function* (data) {
  const { payload } = data;
  yield put({ type: objectionActions.SET_OBJ_NOTES, payload: null });
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_URL}/obj-notes/${encodeURIComponent(
          EncryptFunction(payload?.courseid)
        )}/${encodeURIComponent(EncryptFunction(payload?.examid))}`
      )
    );
    const decryptedData = yield call(handleDecodeJSON, {
      decodeJSON: result?.data?.encryptedData,
    });
    yield put({ type: objectionActions.SET_OBJ_NOTES, payload: decryptedData });
  } catch (err) {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield call(InternalError, err);
  }
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
};

// Response (200):
//   closed window -> { isOpen: false, message }
//   open          -> { isOpen: true, questions: [...] } (one row per question per language)
// 403 means the candidate did not attend - shown in the middle of the page like /obj-notes.
// Any other failure is shown as a toast.
const getObjQuestions = function* (data) {
  const { payload } = data;
  yield put({ type: objectionActions.SET_OBJ_QUESTIONS, payload: null });
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(`${process.env.REACT_APP_API_URL}/obj-questions`, {
        courseid: EncryptFunction(payload?.courseid),
        examid: EncryptFunction(payload?.examid),
      })
    );
    const decryptedData = yield call(handleDecodeJSON, {
      decodeJSON: result?.data?.encryptedData,
    });
    yield put({ type: objectionActions.SET_OBJ_QUESTIONS, payload: decryptedData });
  } catch (err) {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    if (err?.response?.status === 403) {
      yield put({
        type: objectionActions.SET_OBJ_QUESTIONS,
        payload: {
          isOpen: true,
          isPresent: false,
          message: err.response.data?.message || "You have not attended the exam",
        },
      });
    } else {
      yield call(InternalError, err);
    }
  }
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
};

const showSuccess = function* (message) {
  yield put({
    type: commonActions.SET_ALERT,
    payload: { status: "success", show: true, message },
  });
  yield delay(2000);
  yield put({
    type: commonActions.SET_ALERT,
    payload: { status: null, show: false, message: null },
  });
};

// Deletes the objection on one question. Payload: { courseid, examid, questionid }.
const deleteObj = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_BUTTON_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(`${process.env.REACT_APP_API_URL}/delete-objection`, {
        courseid: EncryptFunction(payload?.courseid),
        examid: EncryptFunction(payload?.examid),
        questionid: payload?.questionid,
      })
    );
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
    yield put({ type: objectionActions.SET_OBJ_VIEW, payload: null });
    yield call(showSuccess, result?.data?.message);
  } catch (err) {
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
    yield call(InternalError, toApiError(err));
  }
};

// Objection summary. Response: { rollno, examname, isOpen, isPaid, totalObjections,
// objections: [{ questionid, objectiontype, status, reviewercomment }] }
const getObjSummary = function* (data) {
  const { payload } = data;
  yield put({ type: objectionActions.SET_OBJ_SUMMARY, payload: null });
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(`${process.env.REACT_APP_API_URL}/objection-summary`, {
        courseid: EncryptFunction(payload?.courseid),
        examid: EncryptFunction(payload?.examid),
      })
    );
    const decryptedData = yield call(handleDecodeJSON, {
      decodeJSON: result?.data?.encryptedData,
    });
    yield put({ type: objectionActions.SET_OBJ_SUMMARY, payload: decryptedData });
  } catch (err) {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield call(InternalError, toApiError(err));
  }
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
};

// Objection fee cart. Response (200):
//   open, not paid -> { isOpen: true, isPaid: false, objections, totalObjections, perQuestionFee, totalPayable }
//   paid or closed -> { isOpen, isPaid, message }
const getObjPaymentSummary = function* (data) {
  const { payload } = data;
  yield put({ type: objectionActions.SET_OBJ_PAYMENT_SUMMARY, payload: null });
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(`${process.env.REACT_APP_API_URL}/objection/payment-summary`, {
        courseid: EncryptFunction(payload?.courseid),
        examid: EncryptFunction(payload?.examid),
      })
    );
    const decryptedData = yield call(handleDecodeJSON, {
      decodeJSON: result?.data?.encryptedData,
    });
    yield put({ type: objectionActions.SET_OBJ_PAYMENT_SUMMARY, payload: decryptedData });
  } catch (err) {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield call(InternalError, toApiError(err));
  }
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
};

// Same Airpay hand-off as handleAirpaySubmit in the application fee cart: the decoded result is
// { url, ...hiddenFields }, posted to Airpay through an auto-submitted form.
const redirectToAirpay = ({ url, ...hiddenFields }) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  Object.entries(hiddenFields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value ?? "";
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

// Creates the objection fee order. The amount is calculated on the server from the raised
// objections; only courseid and examid are sent. Payload: { courseid, examid }.
const createObjOrder = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_BUTTON_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(`${process.env.REACT_APP_API_URL}/air-pay/objection/create-order`, {
        courseid: EncryptFunction(payload?.courseid),
        examid: EncryptFunction(payload?.examid),
      })
    );
    const airpayData = yield call(handleDecodeJSON, { decodeJSON: result?.data?.result });
    if (!airpayData?.url) {
      throw new Error("Unable to start the payment. Please try again later");
    }
    // Airpay's return URL (objection-payment-status) carries no course / exam, so keep them here.
    sessionStorage.setItem(
      OBJECTION_PAYMENT_STORAGE_KEY,
      JSON.stringify({
        courseid: payload?.courseid,
        course: payload?.course,
        examid: payload?.examid,
      })
    );
    // The button loader stays on while the browser leaves for Airpay.
    yield call(redirectToAirpay, airpayData);
  } catch (err) {
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
    yield call(InternalError, toApiError(err));
  }
};

// Objection payment status after Airpay redirects back. Payload: { courseid, examid }.
// Response: { isPaymentDone, receiptno, status, totalAmount, paymentdate, refundReason,
// isRefundPending } - status: 0 in progress, 1 success, 2 failed.
const verifyObjPayment = function* (data) {
  const { payload } = data;
  yield put({ type: objectionActions.SET_OBJ_PAYMENT_STATUS, payload: null });
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_URL}/air-pay/objection/verify-payment/${encodeURIComponent(
          EncryptFunction(payload?.courseid)
        )}/${encodeURIComponent(EncryptFunction(payload?.examid))}`
      )
    );
    const decryptedData = yield call(handleDecodeJSON, { decodeJSON: result?.data?.result });
    yield put({ type: objectionActions.SET_OBJ_PAYMENT_STATUS, payload: decryptedData });
  } catch (err) {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield call(InternalError, toApiError(err));
  }
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
};

// Objection fee receipts for the logged-in candidate (candidate taken from the token).
// Same response as the application fee receipts: statusCode 404 means none, not an error.
const getObjReceiptList = function* () {
  try {
    const result = yield call(() =>
      axios.get(`${process.env.REACT_APP_API_URL}/candidate/paymentReceipt/objection`)
    );
    const decryptedData =
      result?.data?.statusCode === 200
        ? yield call(handleDecodeJSON, { decodeJSON: result?.data?.result })
        : [];
    yield put({ type: objectionActions.SET_OBJ_RECEIPT_LIST, payload: decryptedData || [] });
  } catch (err) {
    yield call(InternalError, toApiError(err));
  }
};

// Opens the Raise / View Objection dialog for one question.
// Response: { isRaised, isPaid, isEditable, objection } - objection is null when none is raised.
const getObjView = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(`${process.env.REACT_APP_API_URL}/view-objection`, {
        courseid: EncryptFunction(payload?.courseid),
        examid: EncryptFunction(payload?.examid),
        questionid: payload?.question?.questionid,
      })
    );
    const decryptedData = yield call(handleDecodeJSON, {
      decodeJSON: result?.data?.encryptedData,
    });
    yield put({
      type: objectionActions.SET_OBJ_VIEW,
      payload: { ...decryptedData, question: payload?.question },
    });
  } catch (err) {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield call(InternalError, toApiError(err));
  }
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
};

// Raises or updates the objection. Payload: { courseid, examid, questionid, values, document }.
// document is a File, or null to keep the one already uploaded.
const raiseObj = function* (data) {
  const { payload } = data;
  const formData = new FormData();
  formData.append("courseid", EncryptFunction(payload?.courseid));
  formData.append("examid", EncryptFunction(payload?.examid));
  formData.append("questionid", payload?.questionid);
  Object.entries(payload?.values || {}).forEach(([key, value]) => {
    formData.append(key, value ?? "");
  });
  if (payload?.document) {
    formData.append("document", payload.document);
  }

  yield put({ type: commonActions.SET_BUTTON_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(`${process.env.REACT_APP_API_URL}/raise-objection`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    );
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
    yield put({ type: objectionActions.SET_OBJ_VIEW, payload: null });
    yield call(showSuccess, result?.data?.message);
  } catch (err) {
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
    yield call(InternalError, toApiError(err));
  }
};

export default objectionSaga;
